import React, {useEffect, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {KTSVG} from '../../../template/helpers'
import {cdrAPI} from '../../../services/CDRService'
import CreatableSelect from 'react-select/creatable'
import {Button, Overlay, Popover} from 'react-bootstrap'
import Select, {SingleValue, MultiValue} from 'react-select'
import {updateQuery, resetQuery} from '../slices/lrFilterSlice'
import {columnMetaLr, dataTableConfig} from '../config/data-table'
import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {FilterRule, FilterKeyName, ColumnMeta} from '../core/_models'
import {Option} from '../../types/models'

const genId = () => Math.random().toString(36).slice(2)

export function LrFilter() {
  const {appId} = useParams()
  const dispatch = useAppDispatch()
  const [show, setShow] = useState(false)
  const target = useRef<HTMLButtonElement | null>(null)
  const [filters, setFilters] = useState<FilterRule[]>([])
  const {data: cdrLines} = cdrAPI.useGetLinesQuery(appId!)
  const query = useAppSelector((state) => state.lrFilter)

  useEffect(() => {
    if (query?.rules?.items.length) {
      const savedFilters = query.rules.items
        .map((item) => {
          const meta = columnMetaLr.find((c) => c.key === item.column)
          if (!meta) {
            return null
          }
          const ops = dataTableConfig[meta.type]
          if (!ops) {
            return null
          }
          const opItem = ops.find((o) => o.value === item.operator)
          if (!opItem) {
            return null
          }
          return {
            id: item.id,
            column: item.column,
            type: meta.type,
            operator: opItem.value,
            value: item.value,
          } as FilterRule
        })
        .filter(Boolean) as FilterRule[]
      setFilters(savedFilters)
    }
  }, [query])

  useEffect(() => {
    if (query && !query?.rules) {
      addFilter('did_number')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addFilter = (column: FilterKeyName) => {
    const meta = columnMetaLr.find((c) => c.key === column)
    if (!meta) return

    const firstOperator = dataTableConfig[meta.type][0]

    setFilters((p) => [
      ...p,
      {
        id: genId(),
        column: meta.key,
        type: meta.type,
        operator: firstOperator.value,
        value: meta.type === 'multi-select' ? [] : '',
      },
    ])
  }

  const updateFilter = (id: string, patch: Partial<FilterRule>) => {
    setFilters((p) => p.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }

  const removeAllFilters = () => {
    setFilters([])
    addFilter('did_number')
    dispatch(resetQuery())
    setShow(false)
  }

  const isMultiValueOperator = (op: string) => op === 'inArray' || op === 'notInArray'

  const applyFilters = () => {
    const rulesPayload = filters.map(
      ({id, column, operator, value}) =>
        ({
          id,
          column,
          operator: operator,
          value,
        } as FilterRule)
    )

    dispatch(
      updateQuery({
        ...query,
        rules: {
          operator: '$and',
          items: rulesPayload,
        },
      })
    )
    setShow(false)
  }

  const optionHandler = (meta: ColumnMeta): {name: string}[] => {
    if (meta.key === 'did_number') {
      return cdrLines?.lines ?? []
    }

    return []
  }

  const value = query?.rules?.items[0].value
  const count = Array.isArray(value) ? value.length : value ? 1 : 0

  return (
    <>
      <Button
        ref={target}
        size='sm'
        className='btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary'
        onClick={() => setShow((v) => !v)}
      >
        <i className='bi bi-filter fs-5' />
        Выбрать линии
        {count > 0 && <span className='badge badge-success ms-2'>{count}</span>}
      </Button>

      <Overlay target={target.current} show={show} placement='bottom-start' rootClose onHide={() => setShow(false)}>
        <Popover style={{minWidth: 900, maxWidth: 1200}}>
          <Popover.Header as='div' className='d-flex align-items-center py-1 justify-content-between'>
            Выбрать линии
            <button className='btn btn-sm btn-icon btn-active-color-primary' onClick={() => setShow(false)}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
            </button>
          </Popover.Header>

          <Popover.Body>
            {filters.map((f, i) => {
              const meta = columnMetaLr.find((c) => c.key === f.column)!
              const multiValue = Array.isArray(f.value) ? f.value : []

              return (
                <div key={f.id} className='align-items-center' style={{display: 'flex', gap: 6, marginBottom: 6}}>
                  {i === 0 ? (
                    <span className='badge badge-light-primary min-w-50px justify-content-center'>где</span>
                  ) : (
                    <span className='min-w-50px' />
                  )}
                  <Select
                    className='react-select-styled react-select-solid min-w-200px'
                    classNamePrefix='react-select'
                    options={[]}
                    menuIsOpen={false}
                    value={{label: meta.label, value: meta.key}}
                    isDisabled={true}
                    styles={{
                      indicatorsContainer: () => ({display: 'none'}),
                      indicatorSeparator: () => ({display: 'none'}),
                    }}
                  />
                  <Select
                    className='react-select-styled react-select-solid min-w-150px'
                    classNamePrefix='react-select'
                    options={dataTableConfig[f.type]}
                    value={dataTableConfig[f.type].find((o) => o.value === f.operator)}
                    onChange={(v: SingleValue<Option<string>>) => v && updateFilter(f.id, {operator: v.value})}
                  />
                  <div style={{flex: 1}}>
                    {f.type === 'string' && !isMultiValueOperator(f.operator) && (
                      <input
                        id={f.id}
                        className='form-control'
                        value={f.value as string}
                        placeholder='Введите значение'
                        onChange={(e) => updateFilter(f.id, {value: e.target.value})}
                      />
                    )}

                    {isMultiValueOperator(f.operator) && f.type !== 'multi-select' && (
                      <CreatableSelect<Option<string>, true>
                        key={f.id}
                        isMulti
                        className='react-select-multi-custom react-select-solid'
                        classNamePrefix='react-select'
                        options={optionHandler(meta).map((o: any) => ({label: o.name, value: o.name}))}
                        placeholder='Введите значение и нажмите Enter или Tab'
                        value={multiValue.map((v) => ({
                          label: v,
                          value: v,
                        }))}
                        onChange={(v: MultiValue<Option<string>>) => updateFilter(f.id, {value: v.map((o) => o.value)})}
                      />
                    )}
                  </div>
                </div>
              )
            })}

            <div className='d-flex align-items-center mt-4 gap-2'>
              <Button
                size='sm'
                className='w-100 btn btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary'
                onClick={removeAllFilters}
                disabled={filters.length < 1}
              >
                Сбросить линии
              </Button>
              <Button size='sm' className='w-100' onClick={applyFilters} disabled={filters.length < 1}>
                Применить
              </Button>
            </div>
          </Popover.Body>
        </Popover>
      </Overlay>
    </>
  )
}
